/**
 * Heron Road motion.
 *
 * Three separate mechanisms, deliberately kept apart:
 *
 *   1. Smoothed scroll (Lenis), the ambient feel of the whole site.
 *   2. Reveal on enter, a single fade and rise per element, never repeated.
 *   3. Header collapse, driven by accumulated travel rather than frame delta.
 *
 * Every one of them is disabled under prefers-reduced-motion, and the CSS ships
 * the resting state so nothing depends on this file having run.
 */
(() => {
  'use strict';

  const reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- scroll */

  function startSmoothScroll() {
    if (reduced || !window.Lenis) return null;

    const lenis = new window.Lenis({
      duration: 1.5,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 0.9,
      touchMultiplier: 1.4,
    });

    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);

    window.__hrLenis = lenis;

    // The theme editor and in-page anchors both expect normal scrolling to
    // work, so give them a way to reach the instance.
    document.addEventListener('shopify:section:select', () => lenis.stop());
    document.addEventListener('shopify:section:deselect', () => lenis.start());

    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      window.__hrLenis = null;
    };
  }

  /* ---------------------------------------------------------------- reveal */

  const REVEAL = '.hr-reveal';
  const VISIBLE = 'is-visible';

  function revealAll(root) {
    (root || document).querySelectorAll(REVEAL).forEach((el) => el.classList.add(VISIBLE));
  }

  function startReveals() {
    if (reduced || !('IntersectionObserver' in window)) {
      revealAll();
      return () => {};
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(VISIBLE);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    const observe = (root) => {
      (root || document).querySelectorAll(REVEAL).forEach((el) => {
        if (!el.classList.contains(VISIBLE)) observer.observe(el);
      });
    };

    observe();

    // Sections re-render without a page load in the theme editor, so anything
    // new has to be picked up or it stays invisible while a merchant edits it.
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target) observe(event.target);
    });

    // Anything already past the fold on load should not wait for a scroll.
    requestAnimationFrame(() => {
      document.querySelectorAll(REVEAL).forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight * 0.92) {
          el.classList.add(VISIBLE);
          observer.unobserve(el);
        }
      });
    });

    // Failsafe. Transitions do not advance in a throttled or backgrounded tab,
    // which can leave an element flagged visible and still painted at zero.
    // Anything stranded gets its styles cleared outright rather than waiting on
    // a transition that may never run.
    const rescue = () => {
      document.querySelectorAll(REVEAL).forEach((el) => {
        const near = el.getBoundingClientRect().top < window.innerHeight * 1.5;
        const stalled = el.classList.contains(VISIBLE) && getComputedStyle(el).opacity === '0';
        if (near || stalled) {
          el.classList.add(VISIBLE);
          el.style.opacity = '1';
          el.style.transform = 'none';
          observer.unobserve(el);
        }
      });
    };
    setTimeout(rescue, 2500);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') setTimeout(rescue, 400);
    });

    return () => observer.disconnect();
  }

  /* ---------------------------------------------------------------- header */

  /* ---------------------------------------------------------- header lift */

  /* The bar lifts away while reading down the page and returns the moment the
     visitor scrolls up. The transform goes on the sticky wrapper rather than
     the header inside it, because the wrapper is the element that is actually
     pinned to the top of the viewport.

     Two guards keep it from twitching: nothing happens in the first stretch of
     the page, where the bar should simply be present, and a direction change
     has to be worth more than a few pixels before it counts. */
  function startHeaderLift() {
    const header = document.querySelector('[data-hr-header]');
    if (!header) return () => {};

    /* #header-group is the outermost wrapper and the one that actually sticks,
       so moving it takes the announcement bar and the navigation together.
       The class it carries is shared by the announcement section as well, so
       matching on it by name would pick the wrong element. */
    const shell = document.getElementById('header-group')
      || header.closest('.hr-header-section')
      || header;

    if (header.dataset.hrHide === 'false') return () => {};

    const TOP_ZONE = 140;
    const DELTA = 6;
    let last = window.scrollY || 0;
    let hidden = false;

    const onScroll = () => {
      const y = Math.max(0, window.scrollY || 0);
      const moved = y - last;
      if (Math.abs(moved) < DELTA) return;

      const next = moved > 0 && y > TOP_ZONE;
      last = y;
      if (next === hidden) return;
      hidden = next;
      shell.classList.toggle('is-hidden', hidden);
      /* An open mega menu would otherwise ride up the screen with the bar. */
      if (hidden) closeAllMenus();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }

  function closeAllMenus() {
    document.querySelectorAll('[data-hr-menu]').forEach((menu) => {
      menu.classList.remove('is-open');
      menu.querySelectorAll('[data-hr-menu-trigger]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      menu.querySelectorAll('[data-hr-menu-group]').forEach((g) => { g.hidden = true; });
    });
  }

  /* ---------------------------------------------------------------- pins */

  /**
   * A pinned section should hold the moment its own bottom edge meets the
   * bottom of the viewport, so the reader always reaches the end of it before
   * it stops. That offset is "viewport height minus this section's height",
   * which CSS cannot work out for itself, so measure it here and publish it.
   *
   * A section taller than the viewport gets a negative offset, which is
   * correct: it scrolls through until its bottom lands, then holds showing the
   * end. Nothing is ever unreachable.
   */
  function startPins() {
    const wrappers = [...document.querySelectorAll('.shopify-section')].filter((s) =>
      s.querySelector(':scope > .hr-sticky')
    );
    if (!wrappers.length) return () => {};

    const measure = () => {
      wrappers.forEach((wrapper) => {
        // Read the natural height, not the held one, or the value drifts every
        // time it is recalculated while the section is stuck.
        const previous = wrapper.style.top;
        wrapper.style.top = '';
        const height = wrapper.getBoundingClientRect().height;
        wrapper.style.top = previous;
        wrapper.style.setProperty('--hr-pin-top', Math.round(window.innerHeight - height) + 'px');
      });
    };

    measure();

    // Images and fonts settle after first paint and change section heights.
    window.addEventListener('load', measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    document.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', measure, { once: true });
    });

    let frame = null;
    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', schedule, { passive: true });
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(schedule);
      wrappers.forEach((w) => ro.observe(w));
    }
    document.addEventListener('shopify:section:load', schedule);

    return () => window.removeEventListener('resize', schedule);
  }

  /* ------------------------------------------------------------ mega menu */

  function startMegaMenu() {
    document.querySelectorAll('[data-hr-menu]').forEach((menu) => {
      const panel = menu.querySelector('[data-hr-menu-panel]');
      const triggers = menu.querySelectorAll('[data-hr-menu-trigger]');
      if (!panel || !triggers.length) return;

      let openTimer = null;
      let closeTimer = null;

      const show = (trigger) => {
        clearTimeout(closeTimer);
        const target = trigger.getAttribute('data-hr-menu-trigger');
        panel.querySelectorAll('[data-hr-menu-group]').forEach((group) => {
          group.hidden = group.getAttribute('data-hr-menu-group') !== target;
        });
        triggers.forEach((t) => t.setAttribute('aria-expanded', String(t === trigger)));
        menu.classList.add('is-open');
      };

      const hide = () => {
        closeTimer = setTimeout(() => {
          menu.classList.remove('is-open');
          triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
        }, 120);
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => {
          clearTimeout(openTimer);
          openTimer = setTimeout(() => show(trigger), 60);
        });
        trigger.addEventListener('focus', () => show(trigger));
        trigger.addEventListener('mouseleave', () => {
          clearTimeout(openTimer);
          hide();
        });
      });

      panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      panel.addEventListener('mouseleave', hide);

      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          menu.classList.remove('is-open');
          triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
        }
      });

      // A pointer leaving the header entirely should close it, not leave a
      // panel hanging while the cursor is somewhere else on the page.
      document.addEventListener('mousemove', (event) => {
        if (!menu.classList.contains('is-open')) return;
        if (!menu.contains(event.target)) hide();
      });
    });
  }

  /* --------------------------------------------------- swap image on hover */

  function startHoverImages() {
    document.querySelectorAll('[data-hr-hover-image]').forEach((scope) => {
      const target = scope.querySelector('[data-hr-hover-target]');
      if (!target) return;
      const initial = target.getAttribute('src');

      scope.querySelectorAll('[data-hr-hover-src]').forEach((item) => {
        const src = item.getAttribute('data-hr-hover-src');
        if (!src) return;
        const swap = () => {
          if (target.getAttribute('src') !== src) target.setAttribute('src', src);
        };
        item.addEventListener('mouseenter', swap);
        item.addEventListener('focus', swap);
      });

      scope.addEventListener('mouseleave', () => {
        if (initial) target.setAttribute('src', initial);
      });
    });
  }

  /* --------------------------------------------------- collection filters */

  /* Sorting and filtering are a plain GET form, so the page works with this
     script absent. All this does is remove the step of pressing Apply: a
     changed control submits the form itself. Price is left out, because
     submitting on every keystroke would reload the page mid number. */
  function startFilterForms() {
    document.querySelectorAll('form[data-hr-autosubmit]').forEach((form) => {
      form.addEventListener('change', (event) => {
        const field = event.target;
        if (!field || field.type === 'number') return;
        form.submit();
      });
    });

    /* Only one filter panel open at a time, and a click outside closes it. */
    const panels = [...document.querySelectorAll('.hr-filter')];
    if (!panels.length) return;
    panels.forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        panels.forEach((other) => { if (other !== detail) other.open = false; });
      });
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest('.hr-filter')) return;
      panels.forEach((detail) => { detail.open = false; });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') panels.forEach((detail) => { detail.open = false; });
    });
  }

  /* ------------------------------------------------------------ care tabs */

  /* Progressive enhancement. Every panel is in the flow to begin with, so the
     page is a readable article before this runs and a chooser afterwards.
     Nothing is hidden until there is something able to show it again. */
  function startCareTabs() {
    document.querySelectorAll('[data-hr-care-tabs]').forEach((tablist) => {
      const tabs = [...tablist.querySelectorAll('[data-hr-care-tab]')];
      const panels = tabs
        .map((tab) => document.getElementById(tab.getAttribute('aria-controls')))
        .filter(Boolean);
      if (tabs.length !== panels.length || !tabs.length) return;

      const select = (index, moveFocus) => {
        tabs.forEach((tab, i) => {
          const on = i === index;
          tab.classList.toggle('is-active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          /* Only the selected tab is a tab stop; the rest are reached with the
             arrow keys, which is the expected behaviour for a tablist. */
          tab.tabIndex = on ? 0 : -1;
          panels[i].hidden = !on;
        });
        if (moveFocus) tabs[index].focus();
      };

      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => {
          select(i, false);
          if (history.replaceState) {
            const url = new URL(window.location.href);
            url.hash = tab.id;
            history.replaceState({}, '', url);
          }
        });
        tab.addEventListener('keydown', (event) => {
          const last = tabs.length - 1;
          if (event.key === 'ArrowRight') { event.preventDefault(); select(i === last ? 0 : i + 1, true); }
          if (event.key === 'ArrowLeft') { event.preventDefault(); select(i === 0 ? last : i - 1, true); }
          if (event.key === 'Home') { event.preventDefault(); select(0, true); }
          if (event.key === 'End') { event.preventDefault(); select(last, true); }
        });
      });

      /* A link to one material opens on it. */
      const wanted = tabs.findIndex((t) => '#' + t.id === window.location.hash);
      select(wanted > -1 ? wanted : 0, false);
    });
  }

  /* ------------------------------------------------- narrow screen drawer */

  /* A dialog rather than a dropdown: it covers the page, so focus belongs
     inside it, Escape closes it, and the page behind must not scroll. */
  function startDrawer() {
    const drawer = document.querySelector('[data-hr-drawer]');
    const trigger = document.querySelector('[data-hr-drawer-open]');
    if (!drawer || !trigger) return;

    /* A transformed ancestor becomes the containing block for a fixed child,
       and this header transforms when it lifts away on scroll. Moving the
       drawer to the end of the body puts it out of reach of that. */
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

    const panel = drawer.querySelector('.hr-drawer__panel');
    let scrollY = 0;
    let open = false;

    const focusable = () => [...panel.querySelectorAll(
      'a[href], button:not([disabled]), summary, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )].filter((el) => el.offsetParent !== null);

    const show = () => {
      if (open) return;
      open = true;
      /* Remember where the page was. overflow hidden alone does not hold a
         touch scroll on iOS, so the body is pinned and put back afterwards. */
      scrollY = window.scrollY || 0;
      drawer.hidden = false;
      /* Reading a layout value commits the unhide, so the transform below has
         a previous value to animate from. This used to wait for an animation
         frame, which is not guaranteed to arrive: a browser that has stopped
         scheduling them leaves the panel off screen while focus is already
         trapped inside it, which is a dialog the visitor cannot see or leave. */
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add('hr-drawer-open');
      trigger.setAttribute('aria-expanded', 'true');
      const first = focusable()[0];
      if (first) first.focus();
    };

    const hide = () => {
      if (!open) return;
      open = false;
      drawer.classList.remove('is-open');
      document.body.classList.remove('hr-drawer-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
      trigger.setAttribute('aria-expanded', 'false');
      /* Back to the control that opened it, so a keyboard user is not
         dropped at the top of the document. */
      trigger.focus();
      /* Put it back beyond reach once it has slid away. transitionend is the
         right signal, but it never fires if the transition does not run, so a
         timer backs it up rather than leaving the drawer in the tab order. */
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        if (!open) drawer.hidden = true;
      };
      panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, reduced ? 0 : 600);
    };

    trigger.addEventListener('click', show);
    drawer.querySelectorAll('[data-hr-drawer-close]').forEach((el) => el.addEventListener('click', hide));
    drawer.addEventListener('click', (e) => { if (e.target === drawer) hide(); });

    document.addEventListener('keydown', (e) => {
      if (!open) return;
      if (e.key === 'Escape') { e.preventDefault(); hide(); return; }
      if (e.key !== 'Tab') return;
      /* Keep Tab inside the dialog. */
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* Following a link leaves the page anyway, but the drawer should not be
       left holding the body pinned if the navigation is cancelled. */
    panel.querySelectorAll('a[href]').forEach((a) => a.addEventListener('click', () => hide()));

    /* Back above the phone breakpoint the links are on the bar again, so an
       open drawer would be stranded. */
    window.matchMedia('(min-width: 750px)').addEventListener('change', (e) => { if (e.matches) hide(); });
  }

  /* ------------------------------------------------------------------ boot */

  const boot = () => {
    startSmoothScroll();
    startReveals();
    startHeaderLift();
    startPins();
    startMegaMenu();
    startHoverImages();
    startFilterForms();
    startDrawer();
    startCareTabs();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
